import Phaser from 'phaser';
import { applySceneRenderBudget } from '../render-budget';
import { gameState } from '../state';
import {
  backpackLayout,
  dustHudLayout,
  hudViewport,
  updateHudViewport,
} from '../hud-viewport';
import { onGame, GameEvents } from '../events';
import { playerState, regenCountdownText, tickEnergyRegen } from '../player-state';
import { HUD_FONT } from '../fonts';
import { createEquipmentPanel } from '../ui/equipment-panel';
import { createBottomMenu } from '../ui/bottom-menu';
import { enterCraftingMode, openInventory } from '../crafting/mode';

const HUD_FONT_STYLE = {
  fontFamily: HUD_FONT,
} as const;

const ENERGY_LABEL_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  ...HUD_FONT_STYLE,
  color: 'rgba(255, 255, 180, 0.85)',
};

const ENERGY_VALUE_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  ...HUD_FONT_STYLE,
  color: '#ffe566',
  stroke: '#000000',
  strokeThickness: 4,
};

const REGEN_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  ...HUD_FONT_STYLE,
  color: 'rgba(255, 235, 150, 0.85)',
};

const DUST_VALUE_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  ...HUD_FONT_STYLE,
  color: '#ffffff',
  stroke: '#000000',
  strokeThickness: 2,
};

export class HudScene extends Phaser.Scene {
  hudRoot!: Phaser.GameObjects.Container;

  private energyLabel!: Phaser.GameObjects.Text;
  private energyValue!: Phaser.GameObjects.Text;
  private regenText!: Phaser.GameObjects.Text;
  private dustValue!: Phaser.GameObjects.Text;
  private dustIcon!: Phaser.GameObjects.Image;
  private backpackBtn!: Phaser.GameObjects.Image;
  private anvilBtn!: Phaser.GameObjects.Image;
  private bottomMenu!: ReturnType<typeof createBottomMenu>;
  private equipmentPanel!: ReturnType<typeof createEquipmentPanel>;
  private rightHud: Array<Phaser.GameObjects.Text | Phaser.GameObjects.Image> = [];

  constructor() {
    super({ key: 'HudScene' });
  }

  create() {
    applySceneRenderBudget(this);
    this.cameras.main.setScroll(0, 0);

    this.hudRoot = this.add.container(0, 0).setScrollFactor(0).setDepth(100);

    this.energyLabel = this.add.text(0, 0, 'ENERGY', ENERGY_LABEL_STYLE).setDepth(100);

    this.energyValue = this.add
      .text(0, 0, '', ENERGY_VALUE_STYLE)
      .setDepth(100)
      .setOrigin(0, 0);

    this.regenText = this.add
      .text(0, 0, '', REGEN_STYLE)
      .setDepth(100)
      .setOrigin(0, 0);

    this.dustValue = this.add
      .text(0, 0, '0', DUST_VALUE_STYLE)
      .setDepth(100)
      .setOrigin(1, 0.5)
      .setShadow(0, 4, 'rgba(0,0,0,0.8)', 10, true, false);

    this.dustIcon = this.add
      .image(0, 0, 'hud-dust-icon')
      .setDepth(100)
      .setOrigin(1, 0.5);

    this.backpackBtn = this.add
      .image(0, 0, 'hud-backpack')
      .setDepth(100)
      .setInteractive({ useHandCursor: true });

    this.anvilBtn = this.add
      .image(0, 0, 'hud-anvil')
      .setDepth(100)
      .setInteractive({ useHandCursor: true })
      .setVisible(false);

    this.hudRoot.add([
      this.energyLabel,
      this.energyValue,
      this.regenText,
      this.dustValue,
      this.dustIcon,
      this.backpackBtn,
      this.anvilBtn,
    ]);

    this.backpackBtn.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      openInventory();
    });

    this.anvilBtn.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      enterCraftingMode();
    });

    this.equipmentPanel = createEquipmentPanel(this);
    this.hudRoot.add(this.equipmentPanel.root);

    this.bottomMenu = createBottomMenu(this);
    this.hudRoot.add(this.bottomMenu.root);

    this.rightHud = [this.energyLabel, this.energyValue, this.dustIcon, this.dustValue, this.regenText];

    updateHudViewport(this.game);
    this.applyViewportLayout();
    this.equipmentPanel.refresh();

    onGame(GameEvents.HUD_REFRESH, this.refreshHud, this);
    onGame(GameEvents.PANEL_CHANGED, this.refreshHud, this);
    onGame(GameEvents.INVENTORY_OPEN, this.refreshEquipment, this);

    this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        tickEnergyRegen();
        this.refreshHud();
      },
    });
  }

  applyViewportLayout() {
    if (!this.hudRoot || !this.energyLabel || !this.equipmentPanel) return;

    const layout = dustHudLayout();
    const pack = backpackLayout();

    this.hudRoot.setPosition(hudViewport.x, hudViewport.y);
    this.hudRoot.setScale(1);

    const energyX = layout.paddingSide;
    const energyLabelY = layout.paddingTop;

    this.energyLabel.setPosition(energyX, energyLabelY);
    this.energyLabel.setFontSize(layout.labelSize);

    this.energyValue.setPosition(energyX, energyLabelY + layout.labelSize);
    this.energyValue.setFontSize(layout.energyValueSize);

    this.regenText.setPosition(
      energyX,
      energyLabelY + layout.labelSize + layout.energyValueSize + 8,
    );
    this.regenText.setFontSize(layout.regenSize);

    this.dustValue.setPosition(layout.dustRightX, layout.rowY);
    this.dustValue.setFontSize(layout.dustValueSize);

    this.dustIcon
      .setPosition(layout.dustRightX - layout.dustGap, layout.rowY)
      .setDisplaySize(layout.dustIconSize, layout.dustIconSize);

    this.backpackBtn
      .setPosition(pack.x, pack.y + pack.iconH / 2)
      .setDisplaySize(pack.iconH, pack.iconH);

    this.anvilBtn
      .setPosition(pack.x, pack.y + pack.iconH + pack.gap + pack.iconH / 2)
      .setDisplaySize(pack.iconH, pack.iconH);

    this.equipmentPanel.relayout();
    this.bottomMenu.relayout();
    this.refreshHud();
  }

  refreshHud = () => {
    if (!this.energyValue || !this.dustValue) return;

    this.energyValue.setText(`${playerState.energy}/${playerState.maxEnergy}`);
    this.dustValue.setText(playerState.dust.toLocaleString());

    const regen = regenCountdownText();
    this.regenText.setText(regen);
    this.regenText.setVisible(Boolean(regen));

    const layout = dustHudLayout();
    this.dustValue.setPosition(layout.dustRightX, layout.rowY);
    this.dustIcon.setPosition(layout.dustRightX - this.dustValue.width - layout.dustGap, layout.rowY);

    const leftVisible = gameState.currentPanel === 0;
    const rightVisible = gameState.currentPanel === 1;

    this.backpackBtn.setVisible(leftVisible);
    this.anvilBtn.setVisible(leftVisible);
    this.equipmentPanel.setVisible(leftVisible);
    if (leftVisible) void this.equipmentPanel.refresh();

    for (const obj of this.rightHud) {
      obj.setVisible(rightVisible);
    }
  };

  refreshEquipment = () => {
    this.equipmentPanel?.refresh?.();
  };
}
